import {
	AspectMaintainer, Button, ButtonTypes, Checkbox, Input, PopoverPlacement, Select, Switch
} from "@clintonelec/react-storybook";
import { produce } from "immer";
import type { Dispatch, SetStateAction } from "react";
import { Fragment, memo, useEffect, useState } from "react";
import "Components/ConfigLayout/Camera/OverlaySettings/BannerOverlaySettings/BannerOverlaySettings.less";
import UploadModal from "Components/ConfigLayout/Camera/OverlaySettings/UploadModal";
import { bannerModeOptions, displayModeOptions, locationOptions, timeOptions } from "Data/Objects/BannerOverlay";
import type { BannerDisplayMode, BannerEventTypes, BannerLocation, BannerMode, OverlayState } from "Interfaces";
import { addOrRemoveFromArray } from "@clintonelec/typescriptutils";

type BannerOverlaySettingsProps = {
	localOverlaySettings: OverlayState;
	setLocalOverlaySettings: Dispatch<SetStateAction<OverlayState>>;
};

function BannerOverlaySettings(props: BannerOverlaySettingsProps) {
	const { localOverlaySettings, setLocalOverlaySettings } = props;
	const { bannerOverlay, enableOverlays: mainToggle } = localOverlaySettings;
	const {
		enableOverlay, files, bannerSequenceTime, enableFlash, flashOn, flashOff, bannerMode, displayMode, eventTypes,
		sequenceLocation
	} = bannerOverlay;

	const { file: selectedFile1, location: file1Location } = files[ 0 ];
	const { file: selectedFile2, location: file2Location } = files[ 1 ];
	const disabledSelectedFile2 = !enableOverlay || (!selectedFile1 && !selectedFile2);
	const disabledBannerSequenceTime = !(enableOverlay && selectedFile1 && selectedFile2);
	const [ hiddenImage, setHiddenImage ] = useState(false);
	const uploadRestrictions = `
			Please upload image files in .png or .jpg formats that are under 512KB.
			Files must be no larger than 1920px wide and up to 270px tall.
		`;

	const validateFile = async (file: File) => {
		if (file.size > 512000) {
			return false;
		}

		const image = await createImageBitmap(file);

		if (image.width > 1920 || image.height > 270) {
			return false;
		}

		return true;
	};

	useEffect(() => {
		if (disabledBannerSequenceTime) {
			return;
		}

		const interval = setInterval(() => setHiddenImage(!hiddenImage), bannerSequenceTime * 1000);

		return () => {
			clearInterval(interval);
		};
	}, [ bannerSequenceTime, hiddenImage, disabledBannerSequenceTime ]);

	useEffect(() => {
		if (disabledSelectedFile2 || !enableFlash) {
			return;
		}

		let timeout;

		if (hiddenImage) {
			timeout = setTimeout(() => setHiddenImage(false), flashOff * 1000);
		} else {
			timeout = setTimeout(() => setHiddenImage(true), flashOn * 1000);
		}

		return () => {
			clearTimeout(timeout);
		};
	}, [ hiddenImage, flashOff, flashOn, enableFlash, disabledSelectedFile2 ]);

	const handleEnableOverlayChange = (newEnableOverlay: boolean) => {
		setLocalOverlaySettings(produce(localOverlaySettings, draft => {
			draft.bannerOverlay.enableOverlay = newEnableOverlay;
		}));
	};

	const uploadFile1 = (name: string, dataUrl: string) => {
		setLocalOverlaySettings(produce(localOverlaySettings, draft => {
			draft.bannerOverlay.files[ 0 ] = { ...draft.bannerOverlay.files[ 0 ], file: { name, dataUrl } };

			if (selectedFile2) {
				draft.bannerOverlay.enableFlash = false;
			}
		}));
	};

	const uploadFile2 = (name: string, dataUrl: string) => {
		setLocalOverlaySettings(produce(localOverlaySettings, draft => {
			draft.bannerOverlay.files[ 1 ] = { ...draft.bannerOverlay.files[ 1 ], file: { name, dataUrl } };

			if (selectedFile1) {
				draft.bannerOverlay.enableFlash = false;
			}
		}));
	};

	const handleFileUpdate = (index: number) => (file: string) => {
		setLocalOverlaySettings(produce(localOverlaySettings, draft => {
			if (!file && draft.bannerOverlay.files[ index ]?.file !== null) {
				draft.bannerOverlay.files[ index ] = { ...draft.bannerOverlay.files[ index ], file: null };
			}
		}));
	};

	const handleBannerSequenceTimeSelect = (newBannerSequenceTime: string | null) => {
		setLocalOverlaySettings(produce(localOverlaySettings, draft => {
			draft.bannerOverlay.bannerSequenceTime = Number(newBannerSequenceTime);
		}));
	};

	const handleSequenceLocationSelect = (newLocation: string | null) => {
		setLocalOverlaySettings(produce(localOverlaySettings, draft => {
			draft.bannerOverlay.sequenceLocation = newLocation as BannerLocation;
		}));
	};

	const handleBannerModeSelect = (newBannerMode: string | null) => {
		setLocalOverlaySettings(produce(localOverlaySettings, draft => {
			draft.bannerOverlay.bannerMode = newBannerMode as BannerMode;

			if (newBannerMode === "Sequence" && enableFlash && (!!selectedFile1 && !!selectedFile2)) {
				draft.bannerOverlay.enableFlash = false;
			}
		}));
	};

	const handleFile1LocationSelect = (newLocation: string | null) => {
		setLocalOverlaySettings(produce(localOverlaySettings, draft => {
			draft.bannerOverlay.files[ 0 ] = {
				...draft.bannerOverlay.files[ 0 ], location: newLocation as BannerLocation
			};

			const newFile2Location: BannerLocation = newLocation === "Top" ? "Bottom" : "Top";

			draft.bannerOverlay.files[ 1 ] = {
				...draft.bannerOverlay.files[ 1 ], location: newFile2Location as BannerLocation
			};
		}));
	};

	const handleEnableFlashChange = (newEnableFlash: boolean) => {
		setLocalOverlaySettings(produce(localOverlaySettings, draft => {
			draft.bannerOverlay.enableFlash = newEnableFlash;
		}));

		setHiddenImage(false);
	};

	const handleFlashOnSelect = (newFlashOn: string | null) => {
		setLocalOverlaySettings(produce(localOverlaySettings, draft => {
			draft.bannerOverlay.flashOn = Number(newFlashOn);
		}));
	};

	const handleFlashOffSelect = (newFlashOff: string | null) => {
		setLocalOverlaySettings(produce(localOverlaySettings, draft => {
			draft.bannerOverlay.flashOff = Number(newFlashOff);
		}));
	};

	const handleDisplayModeSelect = (newDisplayMode: string | null) => {
		setLocalOverlaySettings(produce(localOverlaySettings, draft => {
			draft.bannerOverlay.displayMode = newDisplayMode as BannerDisplayMode;
		}));
	};

	const handleEventTypesChange = (eventType: BannerEventTypes) => () => {
		const updatedEventTypes = addOrRemoveFromArray<BannerEventTypes>(Array.from(eventTypes), eventType);

		setLocalOverlaySettings(produce(localOverlaySettings, draft => {
			draft.bannerOverlay.eventTypes = new Set(updatedEventTypes);
		}));
	};

	const renderSequenceBannerImages = (position: BannerLocation) => {
		if (bannerMode !== "Sequence" || position !== sequenceLocation) {
			return;
		}

		const imageClass = [ "image", `image-${ sequenceLocation.toLowerCase() }` ];
		const image1ClassName = [ ...imageClass,
			((hiddenImage && selectedFile2) || (hiddenImage && enableFlash)) ? "hidden" : "" ].join(" ");

		const image2ClassName = [ ...imageClass,
			(!hiddenImage && selectedFile1) || (!hiddenImage && enableFlash) ? "hidden" : "" ].join(" ");

		return (
			<Fragment>
				<img className={ image1ClassName } src={ selectedFile1?.dataUrl } />
				<img className={ image2ClassName } src={ selectedFile2?.dataUrl } />
			</Fragment>
		);
	};

	const renderBannerImage = (position: BannerLocation) => {
		const bannerPositionExists = file1Location === position || file2Location === position;

		if (bannerMode === "Sequence" || !bannerPositionExists) {
			return;
		}

		const flashClass = (enableFlash && hiddenImage) ? "hidden" : "";
		const imageClass = [ "image", `image-${ position.toLowerCase() }`, flashClass ].join(" ");

		if (file1Location === position && selectedFile1) {
			return <img className={ imageClass } src={ selectedFile1?.dataUrl } />;
		}

		if (file2Location === position && selectedFile2 && bannerMode !== "Single") {
			return <img className={ imageClass } src={ selectedFile2?.dataUrl } />;
		}
	};

	const bannerOverlayWrapper = (
		<div className="banner-overlay-wrap">
			<div className="banner">{ renderSequenceBannerImages("Top") }</div>
			<div className="banner">{ renderSequenceBannerImages("Bottom") }</div>
			<div className="banner">{ renderBannerImage("Top") }</div>
			<div className="banner">{ renderBannerImage("Bottom") }</div>
		</div>
	);

	const eventWarning = eventTypes.size < 1
		? <div className="form-row event-type-warning inset">You must enable at least one event type</div>
		: null;

	const commonOptions = (
		<div className="common-options">
			<div className="form-row">
				<span>Display Mode</span>
				<Select
					className="form-select"
					allowClear={ false }
					menuShouldBlockScroll
					menuPlacement={ PopoverPlacement.AUTO }
					menuPortalTarget={ document.body }
					onSelect={ handleDisplayModeSelect }
					options={ displayModeOptions }
					value={ displayMode }
				/>
			</div>
			<div className="form-row">
				<span><span className="inset">Event Type</span></span>
				<div className="event-types">
					<Checkbox
						checked={ eventTypes.has("Motion") }
						onChange={ handleEventTypesChange("Motion") }
						disabled={ displayMode !== "Event" }
					>
						<span>Motion</span>
					</Checkbox>
					<Checkbox
						checked={ eventTypes.has("Analytics") }
						onChange={ handleEventTypesChange("Analytics") }
						disabled={ displayMode !== "Event" }
					>
						<span>Analytics</span>
					</Checkbox>
					<Checkbox
						checked={ eventTypes.has("Alarm In") }
						onChange={ handleEventTypesChange("Alarm In") }
						disabled={ displayMode !== "Event" }
					>
						<span>Alarm In</span>
					</Checkbox>
				</div>
			</div>
			{ eventWarning }
			<div className="form-row">
				<span>Flash</span>
				<div className="form-switch-wrapper">
					<Switch
						checked={ enableFlash }
						disabled={ bannerMode === "Sequence" && (!!selectedFile1 && !!selectedFile2) }
						onChange={ handleEnableFlashChange }
					/>
				</div>
			</div>
			<div className="form-row">
				<span><span className="inset">Flash On Duration</span></span>
				<Select
					className="form-select"
					allowClear={ false }
					disabled={ !enableOverlay || !enableFlash }
					menuShouldBlockScroll
					menuPlacement={ PopoverPlacement.AUTO }
					menuPortalTarget={ document.body }
					onSelect={ handleFlashOnSelect }
					options={ timeOptions }
					value={ flashOn }
				/>
			</div>
			<div className="form-row">
				<span><span className="inset">Flash Off Duration</span></span>
				<Select
					className="form-select"
					allowClear={ false }
					disabled={ !enableOverlay || !enableFlash }
					menuShouldBlockScroll
					menuPlacement={ PopoverPlacement.AUTO }
					menuPortalTarget={ document.body }
					onSelect={ handleFlashOffSelect }
					options={ timeOptions }
					value={ flashOff }
				/>
			</div>
		</div>
	);

	const renderFile2 = () => {
		if (bannerMode === "Single") {
			return;
		}

		return (
			<div className="form-row">
				<span>File 2</span>
				<div className="file-select-wrapper">
					<UploadModal
						uploadFile={ uploadFile2 }
						restrictions={ uploadRestrictions }
						validateFile={ validateFile }
					>
						<Button
							htmlType="button"
							type={ ButtonTypes.TERTIARY }
						>
							Browse
						</Button>
					</UploadModal>
					<Input
						disabled
						placeholder="Upload a file"
						value={ selectedFile2?.name ?? "" }
						clearable
						wrapClassName="file-select-input-wrapper"
						onUpdate={ handleFileUpdate(1) }
					/>
				</div>
			</div>
		);
	};

	const renderSequenceOptions = () => {
		if (bannerMode !== "Sequence") {
			return;
		}

		return (
			<Fragment>
				<div className="form-row">
					<span>Banner Seq. Time</span>
					<Select
						allowClear={ false }
						className="form-select"
						disabled={ disabledBannerSequenceTime }
						menuShouldBlockScroll
						menuPlacement={ PopoverPlacement.AUTO }
						menuPortalTarget={ document.body }
						onSelect={ handleBannerSequenceTimeSelect }
						options={ timeOptions }
						value={ bannerSequenceTime }
					/>
				</div>
				<div className="form-row">
					<span>Location</span>
					<Select
						allowClear={ false }
						className="form-select"
						disabled={ !enableOverlay }
						menuShouldBlockScroll
						menuPlacement={ PopoverPlacement.AUTO }
						menuPortalTarget={ document.body }
						onSelect={ handleSequenceLocationSelect }
						options={ locationOptions }
						value={ sequenceLocation }
					/>
				</div>
			</Fragment>
		);
	};

	const renderFile1Location = () => {
		if (bannerMode === "Sequence") {
			return;
		}

		return (
			<div className="form-row">
				<span>Location</span>
				<Select
					allowClear={ false }
					className="form-select"
					disabled={ !enableOverlay }
					menuShouldBlockScroll
					menuPlacement={ PopoverPlacement.AUTO }
					menuPortalTarget={ document.body }
					onSelect={ handleFile1LocationSelect }
					options={ locationOptions }
					value={ file1Location }
				/>
			</div>
		);
	};

	const renderFile2Location = () => {
		if (bannerMode !== "Dual") {
			return;
		}

		return (
			<div className="form-row">
				<span>Location</span>
				<Select
					allowClear={ false }
					className="form-select"
					disabled
					menuShouldBlockScroll
					menuPlacement={ PopoverPlacement.AUTO }
					menuPortalTarget={ document.body }
					options={ locationOptions }
					value={ file2Location }
				/>
			</div>
		);
	};

	const renderBannerSettings = () => {
		if (!enableOverlay) {
			return <div className="enable-message">Enable Banner Overlay to adjust settings.</div>;
		}

		return (
			<Fragment>
				<div className="form-row">
					<span>Banner Mode</span>
					<Select
						allowClear={ false }
						className="form-select"
						menuShouldBlockScroll
						menuPlacement={ PopoverPlacement.AUTO }
						menuPortalTarget={ document.body }
						onSelect={ handleBannerModeSelect }
						options={ bannerModeOptions }
						value={ bannerMode }
					/>
				</div>
				<div className="form-row">
					<span>File 1</span>
					<div className="file-select-wrapper">
						<UploadModal
							uploadFile={ uploadFile1 }
							restrictions={ uploadRestrictions }
							validateFile={ validateFile }
						>
							<Button
								htmlType="button"
								type={ ButtonTypes.TERTIARY }
							>
								Browse
							</Button>
						</UploadModal>
						<Input
							disabled
							placeholder="Upload a file"
							value={ selectedFile1?.name ?? "" }
							clearable
							wrapClassName="file-select-input-wrapper"
							onUpdate={ handleFileUpdate(0) }
						/>
					</div>
				</div>
				{ renderFile1Location() }
				{ renderFile2() }
				{ renderFile2Location() }
				{ renderSequenceOptions() }
				{ commonOptions }
			</Fragment>
		);
	};

	const renderBannerControls = () => {
		if (!mainToggle) {
			return <div className="enable-message">Enable Overlays to adjust settings.</div>;
		}

		return (
			<Fragment>
				<div className="form-row">
					<Switch
						className="enable-overlay-switch"
						checked={ enableOverlay }
						onChange={ handleEnableOverlayChange }
					/>
					<span>Enable Banner Overlay</span>
				</div>
				{ renderBannerSettings() }
			</Fragment>
		);
	};

	return (
		<div className="banner-overlay-settings columns">
			<div className="column">
				<AspectMaintainer>
					<div className={ `live-snapshot` }>
						<AspectMaintainer>
							<div className="preview-wrapper border">
								<div className="camera-snapshot" />
							</div>
						</AspectMaintainer>
						{ bannerOverlayWrapper }
					</div>
				</AspectMaintainer>
			</div>
			<div className="column">
				{ renderBannerControls() }
			</div>
		</div>
	);
}

export default memo(BannerOverlaySettings);
